import { useState, useEffect } from 'react'

interface RoutineCelebrationProps {
  type: 'task' | 'block' | 'streak'
  message: string
  blockName?: string
  streakCount?: number
  onClose: () => void
}

const RoutineCelebration = ({ type, message, blockName, streakCount, onClose }: RoutineCelebrationProps) => {
  const [isVisible, setIsVisible] = useState(true)
  const [showAnimation, setShowAnimation] = useState(true)

  useEffect(() => {
    // Auto-close after 4 seconds
    const timer = setTimeout(() => {
      handleClose()
    }, 4000)

    return () => clearTimeout(timer)
  }, [])

  const handleClose = () => {
    setIsVisible(false)
    setTimeout(onClose, 500)
  }

  const getCelebrationContent = () => {
    switch (type) {
      case 'task':
        return {
          emoji: '🌸',
          animation: 'bloom',
          color: 'mint',
          title: 'Task Complete!',
          subtitle: message
        }
      
      case 'block':
        return {
          emoji: '🌺',
          animation: 'garden-bloom',
          color: 'peach',
          title: 'Routine Complete!',
          subtitle: message,
          extra: streakCount ? `${streakCount} day streak!` : undefined
        }
      
      case 'streak':
        return {
          emoji: '🔥',
          animation: 'streak-fire',
          color: 'orange',
          title: `${streakCount} Day Streak!`,
          subtitle: message
        }
      
      default:
        return {
          emoji: '✨',
          animation: 'sparkle',
          color: 'sage',
          title: 'Well Done!',
          subtitle: message
        }
    }
  }

  const content = getCelebrationContent()

  if (!isVisible) return null

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4 sm:p-8">
      <div 
        className={`bg-${content.color}-25 rounded-3xl p-6 sm:p-8 max-w-md w-full text-center relative overflow-hidden transition-all duration-500 ${
          isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
      >
        {/* Background Animation */}
        <div className="absolute inset-0 pointer-events-none">
          {content.animation === 'bloom' && (
            <div className="absolute inset-0 flex items-center justify-center">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className={`absolute w-2 h-2 bg-${content.color}-200 rounded-full opacity-60`}
                  style={{
                    animation: `bloom 2s ease-out infinite`,
                    animationDelay: `${i * 0.3}s`,
                    transform: `rotate(${i * 60}deg) translateY(-40px)`
                  }}
                />
              ))}
            </div>
          )}
          
          {content.animation === 'garden-bloom' && (
            <div className="absolute inset-0 flex items-center justify-center">
              {Array.from({ length: 12 }).map((_, i) => (
                <div
                  key={i}
                  className={`absolute w-1 h-1 bg-${content.color}-300 rounded-full opacity-70`}
                  style={{
                    animation: `gardenBloom 3s ease-out infinite`,
                    animationDelay: `${i * 0.2}s`,
                    left: `${20 + Math.random() * 60}%`,
                    top: `${20 + Math.random() * 60}%`
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="relative z-10">
          <div className="text-4xl sm:text-6xl mb-4 sm:mb-6 animate-bounce">
            {content.emoji}
          </div>
          
          <h2 className={`text-${content.color}-600 font-light text-lg sm:text-xl mb-3 sm:mb-4`}>
            {content.title}
          </h2>
          
          <p className={`text-${content.color}-500 font-light text-sm sm:text-base leading-relaxed mb-3 sm:mb-4`}>
            {content.subtitle}
          </p>
          
          {content.extra && (
            <div className={`inline-flex items-center space-x-2 px-3 sm:px-4 py-1 sm:py-2 bg-${content.color}-100/60 rounded-2xl border border-${content.color}-200/40 mb-4 sm:mb-6`}>
              <span className="text-orange-400">🔥</span>
              <span className={`text-${content.color}-600 font-light text-xs sm:text-sm`}>
                {content.extra}
              </span>
            </div>
          )}
          
          <button
            onClick={handleClose}
            className={`px-6 sm:px-8 py-2 sm:py-3 bg-${content.color}-100/60 hover:bg-${content.color}-200/50 
                       text-${content.color}-600 rounded-2xl text-xs sm:text-sm font-light border border-${content.color}-200/40 hover:border-${content.color}-300/50
                       transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-${content.color}-100/50`}
          >
            Continue
          </button>
        </div>

        <style>{`
          @keyframes bloom {
            0% { 
              transform: rotate(var(--rotation)) translateY(-20px) scale(0);
              opacity: 0;
            }
            50% { 
              opacity: 1;
            }
            100% { 
              transform: rotate(var(--rotation)) translateY(-60px) scale(1);
              opacity: 0;
            }
          }
          
          @keyframes gardenBloom {
            0% { 
              transform: scale(0) translateY(0);
              opacity: 0;
            }
            30% { 
              opacity: 1;
            }
            100% { 
              transform: scale(1.5) translateY(-30px);
              opacity: 0;
            }
          }
        `}</style>
      </div>
    </div>
  )
}

export default RoutineCelebration